import {RouterController} from "../components/router/router-controller";
import {CompetitorDomainModel} from "../../domain/common/competitor/models/competitor-domain-model";
import {RouteModel} from "../components/router/models/route-model";
import {SITE_DOMAINS} from "../consts";
import {BaseService} from "../../infrastructure/domain-services/base-service";
import {TermsUtils} from "../../infrastructure/utils/terms-utils";
import {CompetitionDomainModel} from "../../domain/common/competition/models/competition-domain-model";
import {FullGameDomainModel} from "../../domain/full-game/models/full-game-domain-model";
import {GameCenterLinkModel} from "../../containers/game-center-container/models/game-center.link-model";
import {PersonSchemaModel} from "../models/schema/person-schema-model";
import {EntityDomainModel} from "../../infrastructure/models/entity/entity.domain-model";
import {OrganizationSchemaDataModel, OrganizationSchemaModel} from "../models/schema/organization-schema-model";
import {SportOrganizationSchemaDataModel, SportOrganizationSchemaModel} from "../models/schema/sport-organization-schema-model";
import {SportTeamSchemaDataModel, SportTeamSchemaModel} from "../models/schema/sport-team-schema-model";
import {SportEventSchemaDataModel, SportEventSchemaModel} from "../models/schema/sport-event-schema-model";
import {BreadcrumbsListSchemaModel} from "../models/schema/breadcrumbs-list-schema-model";
import {BreadcrumbsItemModel} from "./breadcrumbs/breadcrumbs.item-model";
import {LogoImageParams} from "../components/logo-icon/models/logo.image-params";
import {BaseSchemaModel} from "../models/schema/base-schema-model.js";
import {LocationSchemaDataModel, LocationSchemaModel} from "../models/schema/location-schema-model";
import {ENTITY_LOGO_SIZE, EVENT_ATTENDANCE_MODE, SCHEMA_EVENT_STATUS, SCHEMA_GAME_DURATION_BY_SPORT, SCHEMA_TYPE} from '../models/schema/consts';
import {CompetitorLogoParams} from "../../domain/common/competitor/models/competitor-logo-params";
import {CompetitionLogoParams} from "../../domain/common/competition/models/competition-logo-params";
import {SchemaLogoImageParams} from "../components/logo-icon/models/schema-logo.image-params";
import {GameImageRequestParams} from "../../domain/partial-game/models/game-image-request-params";
import {AddressSchemaDataModel, AddressSchemaModel} from "../models/schema/address-schema-model";
import {DatetimeUtils} from "../../infrastructure/utils/datetime-utils";
import {AthleteLogoParams} from "../../domain/common/athlete/athlete-logo-params";
import {NationalitySchemaModel} from "../models/schema/nationality-schema-model";
import {AthleteDomainModel} from "../../domain/common/athlete/athlete-domain-model";
import {OrganizerSchemaDataModel, OrganizerSchemaModel} from "../models/schema/organizer-schema-model";
import {AthleteLinkModel} from "../../containers/athlete-container/models/athlete-link-model";
import {AffiliationSchemaModel} from "../models/schema/affiliation-schema-model";
import {FaqPageSchemaModel} from "../models/schema/faq-page-schema-model";
import type {EntityDescriptionFaqElementViewModel} from "../../modules/entity-description-module/models/entity-description.faq.element.view-model";
import {GameCenterHashParamsModel} from "../../containers/game-center-container/models/game-center.hash-params-model";
import {CompetitionLinkModel} from "../../containers/competition-container/models/competition.link-model";
import {CompetitorLinkModel} from "../../containers/competitor-container/models/competitor-link-model";

export class SchemaManager extends BaseService {
  static injectables = [...BaseService.injectables, RouterController, TermsUtils, DatetimeUtils];

  constructor(routerController: RouterController, termsUtils: TermsUtils, datetimeUtils: DatetimeUtils) {
    super();

    this.routerController = routerController;
    this.termsUtils = termsUtils;
    this.datetimeUtils = datetimeUtils;
  }

  get terms() {
    return this.termsUtils.terms;
  }

  createBreadcrumbs(list: Array<BreadcrumbsItemModel>): BreadcrumbsListSchemaModel {
    return new BreadcrumbsListSchemaModel({list});
  }

  createOrganization(): OrganizationSchemaModel {
    const logo: LogoImageParams = new SchemaLogoImageParams(250, 250);
    const id: String = `${SITE_DOMAINS.full}/pages/about`;

    const schemaModel: OrganizationSchemaDataModel = {
      url: SITE_DOMAINS.full,
      name: '365Scores',
      knowsAbout: this.terms.SEO_SD_ORGANIZATION_KNOWSABOUT,
      sameAs: this.terms.SEO_SD_ORGANIZATION_SAMEAS,
      logo: logo.toString(),
      description: this.terms.SEO_SD_ORGANIZATION_DESCRIPTION,
      legalName: '365Scores LTD.',
      id
    }

    const schema: OrganizationSchemaModel = new OrganizationSchemaModel(schemaModel);

    return schema;
  }

  createSportOrganization(competition: CompetitionDomainModel): SportOrganizationSchemaModel {
    const location: LocationSchemaModel = this.createLocation(competition?.country.name);
    const logo: CompetitionLogoParams = new CompetitionLogoParams({
      width: ENTITY_LOGO_SIZE.width,
      height: ENTITY_LOGO_SIZE.height
    }, competition)

    const schemaModel: SportOrganizationSchemaDataModel = {
      url: this.createCompetitionUrl(competition),
      location,
      logo,
      id: competition?.id,
      name: competition?.name,
      sport: competition?.sport.name
    }

    const schema: SportOrganizationSchemaModel = new SportOrganizationSchemaModel(schemaModel);

    return schema;
  }

  createSportTeam(competitor: CompetitorDomainModel, squad: Array<AthleteDomainModel>): BaseSchemaModel {
    let schema: BaseSchemaModel;

    if (competitor.isAthlete) {
      schema = this.createPerson(competitor, true);
    } else {
      const linkModel: CompetitorLinkModel = new CompetitorLinkModel(competitor.id, competitor.sport.nameForURL, competitor.nameForURL, competitor.isFollow);
      const routeModel: RouteModel = this.routerController.findRouteByName(linkModel.name);
      const url: String = `${SITE_DOMAINS.full}${this.routerController.navigationManager.createUrl(routeModel.path, linkModel)}`;
      const location: LocationSchemaModel = this.createLocation(competitor.countryName);
      const coach: PersonSchemaModel = this.createCoaches(squad);
      const athlete = squad?.filter((athlete: AthleteDomainModel) => !athlete.isStaff).map(person => this.createPerson(person));
      const logo: CompetitorLogoParams = new CompetitorLogoParams({
        width: ENTITY_LOGO_SIZE.width,
        height: ENTITY_LOGO_SIZE.height
      }, competitor)

      const schemaModel: SportTeamSchemaDataModel = {
        url,
        name: competitor.name,
        id: competitor.id,
        sport: competitor.sportName,
        location,
        logo,
        coach,
        athlete
      }

      schema = new SportTeamSchemaModel(schemaModel);
    }

    return schema;
  }

  createPerson(entity: EntityDomainModel, isCompetitor): PersonSchemaModel {
    let url: String = null;
    let clubUrl: String = null;
    let image: AthleteLogoParams = null;
    let affiliation: BaseSchemaModel = null;

    if(isCompetitor) {
      const linkModel: CompetitorLinkModel = new CompetitorLinkModel(entity.id, entity.sport.nameForURL, entity.nameForURL, entity.isFollow);
      const routeModel: RouteModel = this.routerController.findRouteByName(linkModel.name);
      url = `${SITE_DOMAINS.full}${this.routerController.navigationManager.createUrl(routeModel.path, linkModel)}`;
    } else {
      const linkModel: AthleteLinkModel = new AthleteLinkModel(entity.id, entity.sport.nameForURL, entity.nameForURL, entity.isNational, null, null, null, entity?.isFollow);
      const routeModel: RouteModel = this.routerController.findRouteByName(linkModel.name);
      url = `${SITE_DOMAINS.full}${this.routerController.navigationManager.createUrl(routeModel.path, linkModel)}`;
      image = new AthleteLogoParams({
        width: ENTITY_LOGO_SIZE.width,
        height: ENTITY_LOGO_SIZE.height
      }, entity)
    }
    if (entity.club) {
      const linkModel: CompetitorLinkModel = new CompetitorLinkModel(entity.club.id, entity.club.sport.nameForURL, entity.club.nameForURL, entity.club.isFollow);
      const routeModel: RouteModel = this.routerController.findRouteByName(linkModel.name);
      clubUrl = `${SITE_DOMAINS.full}${this.routerController.navigationManager.createUrl(routeModel.path, linkModel)}`;
      affiliation = new AffiliationSchemaModel({name: entity.club.name, url: clubUrl});
    }
    const nationality: NationalitySchemaModel = new NationalitySchemaModel({name: entity.country.name})

    const schemaModel: PersonSchemaModel = {
      url,
      image,
      nationality,
      name: entity.name,
      gender: entity.gender,
      birthDate: entity.birthdate,
      affiliation
    }

    const schema: PersonSchemaModel = new PersonSchemaModel(schemaModel);

    return schema;
  }

  createSportEvent(game: FullGameDomainModel): SportEventSchemaModel {
    const hashParams: GameCenterHashParamsModel = new GameCenterHashParamsModel({id: game.id});
    const linkModel: GameCenterLinkModel = new GameCenterLinkModel(game.matchupId, game.sport.nameForURL, game.competition.nameForURL, game.nameForUrl, hashParams, game.isFollow);
    const routeModel: RouteModel = this.routerController.findRouteByName(linkModel.name);
    const url: String = `${SITE_DOMAINS.full}${this.routerController.navigationManager.createUrl(routeModel.path, linkModel)}`;

    const startDate: String = game.startTime.toISOString();
    const endDate: String = this.createEndDate(game)?.toISOString();
    const name: String = this.terms.WEB_MATCHUP_INPAGE_TITLE_TEMPLATE?.format({
      "HOME_COMPETITOR": game.homeCompetitor.name,
      "GAME_TITLE_VS": this.terms.GAME_TITLE_VS,
      "AWAY_COMPETITOR": game.awayCompetitor.name,
    });

    const location: LocationSchemaModel = this.createLocation(game.venue?.name || game.competition.countryName, game.venue?.capacity, game.competition.countryName)
    const eventStatus: SCHEMA_EVENT_STATUS = SCHEMA_EVENT_STATUS.scheduled;
    const description: String = game.competitionDisplayName;
    const logo: GameImageRequestParams = new GameImageRequestParams(game);
    const organizer: OrganizerSchemaModel = this.createOrganizer(game.competition);
    const eventAttendanceMode: String = EVENT_ATTENDANCE_MODE.offline;

    const schemaModel: SportEventSchemaDataModel = {
      url,
      name,
      startDate,
      endDate,
      location,
      eventStatus,
      description,
      logo,
      homeSportTeam: game.homeCompetitor.isAthlete ? this.createPerson(game.homeCompetitor, true) : this.createSportTeam(game.homeCompetitor),
      awaySportTeam: game.awayCompetitor.isAthlete ? this.createPerson(game.awayCompetitor, true) : this.createSportTeam(game.awayCompetitor),
      eventAttendanceMode,
      organizer
    }

    const schema: SportEventSchemaModel = new SportEventSchemaModel(schemaModel);

    return schema;
  }

  createLocation(location: String, attendance: Number, addressCountry: String) {
    const schemaModel: LocationSchemaDataModel = {
      name: location,
      maximumAttendeeCapacity: attendance
    }

    if (addressCountry) {
      schemaModel.address = this.createAddress(addressCountry);
    }

    const schema: LocationSchemaModel = new LocationSchemaModel(schemaModel);

    return schema;
  }

  createAddress(addressCountry: String) {
    const schemaModel: AddressSchemaDataModel = {
      addressCountry
    }

    const schema: AddressSchemaModel = new AddressSchemaModel(schemaModel);

    return schema;
  }

  createEndDate(game) {
    let endDate = null;

    const sportType = game.sport?.id;
    const startTime: Date = game.startTime;
    const gameDuration = SCHEMA_GAME_DURATION_BY_SPORT[sportType];

    if (gameDuration) {
      endDate = this.datetimeUtils.addMinutes(startTime, gameDuration);
    }

    return endDate;
  }

  createCoaches(squad: Array<AthleteDomainModel>) {
    let coachAthletes: Array<AthleteDomainModel> = squad?.filter((athlete: AthleteDomainModel) => athlete.isStaff);
    let coaches: Array<PersonSchemaModel> = [];

    if (coachAthletes?.length > 0) {
      coaches = coachAthletes.map(coach => this.createPerson(coach))
    }

    return coaches
  }

  createOrganizer(competition: CompetitionDomainModel): OrganizerSchemaModel {
    const schemaModel: OrganizerSchemaDataModel = {
      name: competition.name,
      url: this.createCompetitionUrl(competition)
    }

    const schema: OrganizerSchemaModel = new OrganizerSchemaModel(schemaModel);

    return schema;
  }

  createCompetitionUrl(competition: CompetitionDomainModel): String {
    const linkModel: CompetitionLinkModel = new CompetitionLinkModel(competition.id, competition.sport.nameForURL, competition.nameForURL, competition.isFollow);
    const routeModel: RouteModel = this.routerController.findRouteByName(linkModel.name);
    const url: String = `${SITE_DOMAINS.full}${this.routerController.navigationManager.createUrl(routeModel.path, linkModel)}`;

    return url;
  }

  createFAQPage(elements: Array<EntityDescriptionFaqElementViewModel>): FaqPageSchemaModel {
    let mainEntity = [];

    elements?.forEach((element: Array<EntityDescriptionFaqElementViewModel>) => mainEntity.push({question: element.question.value, answer: element.answer.value}))

    const schema: FaqPageSchemaModel = new FaqPageSchemaModel({mainEntity});

    return schema;
  }

  createWebsite(): BaseSchemaModel {
    const schemaModel: BaseSchemaModel = {
      url: SITE_DOMAINS.full,
      name: '365Scores',
      type: SCHEMA_TYPE.website
    }

    const schema: BaseSchemaModel = new BaseSchemaModel(schemaModel.type, schemaModel.name, schemaModel.url);

    return schema;
  }
}
